// Storage key constants for session/local persistence.
// Extracted from App.jsx and restore.js for single-source-of-truth.

export const PENDING_FORECAST_KEY = "pendingForecast";
export const ACTIVE_FORECAST_KEY = "soothActiveForecastId";
export const TOUR_STATE_KEY = "sooth-tour-state";
// Set when the tour reaches its terminal "examples" step (the "now submit your own
// question" invitation). The tour itself ends there (tour state is cleared), but the
// example-questions reveal is transient UI — this flag re-shows it on reload until the
// user submits their own forecast or clears.
export const POST_TOUR_EXAMPLES_KEY = "sooth-post-tour-examples";

// Run ids the user explicitly DETACHED via CLEAR. A detached run keeps running
// and lands in the log, but the reload resume-sweep must NOT auto-focus it (CLEAR
// means "I'm done looking at this", so a reload should stay on a fresh chat, not
// refocus the still-running run via the sweep's most-recent-running-root focus
// fallback). Stored as a JSON id array in sessionStorage (per-tab). Cleared per-id
// when the user re-opens that forecast from the log.
export const DETACHED_RUNS_KEY = "sooth-detached-runs";

/** @returns {Set<string>} the currently-detached run ids (empty on any error). */
export function readDetachedRunIds() {
  try {
    const raw = sessionStorage.getItem(DETACHED_RUNS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

/** Mark a run id as detached (no-op for a falsy id). */
export function addDetachedRunId(id) {
  if (!id) return;
  try {
    const s = readDetachedRunIds();
    s.add(id);
    sessionStorage.setItem(DETACHED_RUNS_KEY, JSON.stringify([...s]));
  } catch {
    /* private mode / quota — best-effort */
  }
}

/** Clear a run id's detached mark — e.g. when the user re-opens it from the log. */
export function removeDetachedRunId(id) {
  if (!id) return;
  try {
    const s = readDetachedRunIds();
    if (s.delete(id)) sessionStorage.setItem(DETACHED_RUNS_KEY, JSON.stringify([...s]));
  } catch {
    /* private mode / quota — best-effort */
  }
}

// The updates this tab started on forecasts SHARED with it (an in-org collaborator's Update
// now): `{[runId]: {traceId}}`, per-tab like PENDING_FORECAST_KEY. The boot resume sweep covers
// only the viewer's own forecasts and runs before the shared list loads, so this is what lets a
// reload mid-update reattach once those runs hydrate (resumeSharedReruns). One entry per run, so
// two overlapping updates keep their own.
export const SHARED_RERUN_KEY = "sooth-shared-rerun";

/** @returns {Record<string, {traceId: string}>} the stored entries ({} when absent/unreadable). */
function readSharedRerunMap() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(SHARED_RERUN_KEY) || "{}");
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
    const out = {};
    for (const [runId, entry] of Object.entries(raw)) {
      if (entry && typeof entry.traceId === "string") out[runId] = { traceId: entry.traceId };
    }
    return out;
  } catch {
    return {};
  }
}

/** @param {Record<string, {traceId: string}>} map */
function writeSharedRerunMap(map) {
  try {
    if (Object.keys(map).length) sessionStorage.setItem(SHARED_RERUN_KEY, JSON.stringify(map));
    else sessionStorage.removeItem(SHARED_RERUN_KEY);
  } catch {
    /* private mode / quota — best-effort */
  }
}

/** @returns {Array<{runId: string, traceId: string}>} every recorded shared update. */
export function readSharedRerunHandles() {
  return Object.entries(readSharedRerunMap()).map(([runId, { traceId }]) => ({ runId, traceId }));
}

/**
 * @param {string|null|undefined} runId
 * @returns {{runId: string, traceId: string}|null} the update recorded for `runId`, or null.
 */
export function sharedRerunHandleFor(runId) {
  const entry = runId ? readSharedRerunMap()[runId] : null;
  return entry ? { runId, traceId: entry.traceId } : null;
}

/**
 * Record a shared update this tab started (no-op without both ids).
 * @param {string|null|undefined} runId
 * @param {string|null|undefined} traceId
 */
export function writeSharedRerunHandle(runId, traceId) {
  if (!runId || !traceId) return;
  writeSharedRerunMap({ ...readSharedRerunMap(), [runId]: { traceId } });
}

/**
 * Clear `runId`'s entry (only when it is on `traceId`, when given), leaving every other run's.
 * @param {string} runId
 * @param {string|null} [traceId]
 */
export function clearSharedRerunHandle(runId, traceId = null) {
  const map = readSharedRerunMap();
  const entry = map[runId];
  if (!entry || (traceId && entry.traceId !== traceId)) return;
  delete map[runId];
  writeSharedRerunMap(map);
}
